<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" class="pa-0">
        <div :id="rootElementId" ></div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import * as d3v4 from "d3v4";
import { round, getDataUrl } from '../../utils/functions';
import ResizeObserver from 'resize-observer-polyfill';
import { loadFonts,copyAndAppendElement } from '../../utils/exportToPNG';
import Dictionary from '../../dictionary';

export default {
  name: 'StackedBarChart',
  props: {
    id:{
      type:String,
    },
    teamId:{
      type: Number,
      required: true
    },
    teamName:{
      type: String,
      required: false
    },
    gameName:{
      type: String,
      required: false
    },
    seasonName:{
      type: String,
      required: false
    },
    hwRatio: {
      type: Number,
      required: false
    },
    title: {
      type: Boolean,
      required: false
    },
    size: {
      type: Number,
      //556 is the StackedBarchart size for laptop screen
      default: 556
    },
    margin: {
      type: Object,
    },
    numberPlayersToShow: {
      type: Number,
      default: 10
    },
    language: {
      type: String,
      required: true
    },
    //Stats to show based on select choice
    barChartStats: {
      type: Object,
      required: true,
    },
    isTotal: {
      type: Boolean,
      default: true
    },
    //Original data. All stats player by player
    chartData: {
      type: Array,
      required: true,
    },
    printLogo: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: Number,
      default: 20
    },
    chartType: {
      type: String,
      required: false,
    },
    noDataText: {
      type: String,
      default: "No Data"
    }
  },
  data() {
    return {
      observer: null,
      images: {},
      //Data to show in stacked bar chart
      barChartData: [],
    }
  },
  computed: {
    darkMode(){
      return this.$vuetify.theme.dark;
    },
    rootElementId(){
      if(this.id)
        return `barChart${this.teamId}-${this.id}`;
      else
        return `barChart${this.teamId}`;
    },
    currentUser() {
      return this.$store.state.auth.user;
    }
  },
  async mounted() {
    this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));
    this.barChartData = this.chartData;
    this.drawChart();
  },
  methods: {
    drawImageBase64(svg, x, y, width, height, img, bottom = false) {
      let base_image = new Image();
      base_image.crossOrigin = "anonymous";
      base_image.src = img;
      base_image.onload = event => {
        const dataUrl = getDataUrl(event.currentTarget);
        this.drawImage(svg, x, y, width, height, dataUrl, bottom);
      };
    },
    async drawChart(){
      var containsNegativeValues = false;
      var isAllNegative = true;
      //Config data
      var cfg = {
        labelFontSize: this.fontSize, //Font size of the labels
        legendFontSize: this.fontSize, //Font size of the legend
        wrapWidthYLabel: 110 - (this.fontSize > 20 ? 10 : 0), // Max Width for labels of Y axis
        borderStroke: 2,
        borderStrokeHover: 4,
        legendSquareLength: 40,
      };

      // Check if is mobile device
      const isMobileDevice = this.size < 440 ? 1 : 0;
      const margin = this.margin ? this.margin : {top: 0, right: this.size * 0.2, bottom: 25, left: this.size * 0.21};
      const SVG_WIDTH = this.size;
      const SVG_HEIGHT = SVG_WIDTH * (this.hwRatio ? this.hwRatio : 0.75)  - (this.printLogo ? 0 : 30);
      const CHART_WIDTH = SVG_WIDTH - margin.left - margin.right;
      const CHART_HEIGHT = SVG_HEIGHT - margin.top - margin.bottom  - (this.printLogo ? 0 : 30);
      // Draw Driblab Logo
      const LOGO_WIDTH = SVG_WIDTH * 0.187;
      const LOGO_HEIGHT = SVG_HEIGHT * 0.085;

      const ratio = SVG_WIDTH <= 380 ? 0.0025 : SVG_WIDTH < 700 ? 0.0018 : 0.0015;
      const CHART_SIZE_RATIO = (SVG_WIDTH/2) * ratio;
      const DECIMALS = this.isTotal ? this.barChartStats.decimals : 2;
      let keys = this.barChartStats.stats.map(s => this.isTotal ? s.code : s.code + "90");
      //Remove whatever chart with the same id/class was present before
      d3v4.select(`#${this.rootElementId}`).select("svg").remove();

      var svg = d3v4.select(`#${this.rootElementId}`).append("svg")
                .attr("id",`${this.rootElementId}SVG`)
                .attr("width",  SVG_WIDTH)
                .attr("height", SVG_HEIGHT);

      var g = svg.append("g").attr("transform", "translate(" + margin.left + "," + margin.top + ")");

      //chartData contains original data, with all players
      this.chartData.forEach((player) => {
        let total = 0;
        let totalNegative = 0;
        this.barChartStats.stats.forEach((stat) => {
          let code = stat.code;
          if(!this.isTotal){
            //If is per90, add '90' to the code to get per 90 stats data
            code += "90";
          }
          switch (code) {
            case "totalFinishing":
              player[code] = player.totalShots > 0 ? player.goals/player.totalShots * 100 : 0;//round(player[code], DECIMALS);
              break;
            case "nonPenaltyFinishing":
              player[code] = player.nonPenaltyShots > 0 ? player.npg/player.nonPenaltyShots * 100 : 0;
              break;
            case "goalsPenaltyFinishing":
              player[code] = player.penaltyShots > 0 ? player.goalsPenalty/player.penaltyShots * 100 : 0;
              break;
            default:
              //Rest of the stats
              player[code] = round(player[code], DECIMALS);
          }

          if(stat.isNegative){
            //Just take into account for totalNegative if is a total stat (exclude open play and set piece stats)
            totalNegative += player[code];
            containsNegativeValues = true;
          }else{
            //Just take into account for total if is a total stat (exclude open play and set piece stats)
            total += player[code];
          }
        });
        player.total = round(total, DECIMALS);
        player.totalNegative = round(totalNegative, DECIMALS);
        player.totalNet = player.totalNegative + player.total;
        if(player.totalNet != player.totalNegative){
          isAllNegative = false;
        }
      });

      //Sort array
      if(containsNegativeValues){
        //If contains negative values, sort array descending by totalNegative
        this.chartData.sort(function(a, b) { return isAllNegative ? a.totalNet - b.totalNet : b.totalNet - a.totalNet; });
      }else{
        //If just contains positive values, sort descending by total
        this.chartData.sort(function(a, b) { return b.total - a.total; });
      }
      //Show just numberPlayersToShow value. Default show 10 players
      this.barChartData =  this.chartData.filter(s => s.totalNet != 0).slice(0, this.numberPlayersToShow);
      
      let numBars = this.barChartData.length < this.numberPlayersToShow ? this.barChartData.length : this.numberPlayersToShow;
      if(this.barChartData.length){
        let minX = d3v4.min(this.barChartData, function(d) { return d.totalNegative; });
        if(minX < 0){
          if(this.isTotal){
            //Total
            minX = minX > -0.2 ? -0.3 : minX > -1 ? minX *= 3 : minX *= 2;
          }else{
            //per 90'
            minX = minX > -0.2 ? -0.3 : minX *= 2;
          }
        }

        let maxX = d3v4.max(this.barChartData, function(d) { return d.total; });
        maxX = maxX > 0 && DECIMALS > 0 ? (maxX + maxX/4) : maxX + 1;
        //If is a perecentage stat, then set 100% as max value on X axis
        if(this.barChartStats.isPercentage && maxX > 100){
          maxX = 100;
        }

        //Number of ticks for X axis
        let numberOfTicks = 5

        // Create scale for X and Y axis
        let maxRange = isMobileDevice ? CHART_WIDTH - margin.right/2 : CHART_WIDTH;
        const scaleX = d3v4.scaleLinear()
          .range([0, maxRange])
          .domain([minX, maxX])
          .nice()

        // X Axis: Adjust height of axis to number of players to show
        const axisX = d3v4.axisBottom(scaleX).tickSize(-CHART_HEIGHT).ticks(numberOfTicks).tickSizeOuter(0);

        const scaleY = d3v4.scaleBand()
            .range([CHART_HEIGHT - (CHART_HEIGHT/numBars * this.barChartData.length), CHART_HEIGHT])
            .domain(this.barChartData.map(function(d) { return d.playerName; }))
            .align(1);

        const axisY = d3v4.axisLeft(scaleY).tickSizeOuter(0);

        g.append("g")
            .attr("transform", "translate(0,0)")
            .call(axisY)
            .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
            .style("font-family", "NeueMontreal")
            .selectAll(".tick text")
            .call(this.wrap, isMobileDevice ? (cfg.wrapWidthYLabel * 0.6) : cfg.wrapWidthYLabel)
            .style("cursor", "pointer")
            .style("fill",this.darkMode ? '#fff' : '#000')
            .on("click", d => {
              let item = this.barChartData.filter(p => p.playerName === d)[0];
              let url = item.playerId ? "/player/" + item.playerId : (item.id ? "/player/" + item.id : "/team/" + item.teamId)
              //Open player or team page on the same tab (redirect)
              window.open(url, "_self");
            });
        let axixText = this.isTotal ? "axisXTotal" : "axisXper90";
        g.append("g")
            .attr("transform", "translate(0," + (CHART_HEIGHT) + ")")
            .attr("stroke-width", "2")
            .style("stroke-dasharray", ("10, 2"))
            .style("shape-rendering", "crispEdges")
            .attr("class", "axis")
            .call(axisX)
            .selectAll(".tick text")
            .style("fill",this.darkMode ? '#fff' : '#000')
            .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
            .style("font-family", "NeueMontreal")
            .style("shape-rendering", "crispEdges")
          .append("text")
            .attr("x", CHART_WIDTH * 1.5)
            .attr("dy", "5em")
            .attr("text-anchor", "middle")
            .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
            .style("font-family", "NeueMontreal")
            .style("font-weight", "bold")
            .style("shape-rendering", "crispEdges")
            .text(Dictionary[this.language]['team']['overviewTab'][axixText])
          .attr("transform", "translate("+ (-CHART_WIDTH) +",-10)")
          .selectAll('line').attr('stroke', 'blue')


        // DRAW Title
        if (this.title) {
          g.append("text")
            .attr("x", 0)
            .attr("y", -CHART_WIDTH * 0.135)
            .attr("text-anchor", "left")
            .style("font-size", (30 * CHART_SIZE_RATIO) + "px")
            .style("font-family", "NeueMontreal")
            .style("color", this.darkMode ? "white" : "black")
            .style("fill",'currentColor')
            .text(this.teamName + " · " + Dictionary[this.language]['faq']['metrics']['categories']['abbr'][this.chartType]);
          g.append("text")
            .attr("x", 0)
            .attr("y", -CHART_WIDTH * 0.08)
            .attr("text-anchor", "left")
            .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
            .style("font-family", "NeueMontreal")
            .style("color", this.darkMode ? "white" : "black")
            .style("fill",'currentColor')
            .text(this.gameName);
          g.append("text")
            .attr("x", 0)
            .attr("y", -CHART_WIDTH * 0.025)
            .attr("text-anchor", "left")
            .style("font-size", (24 * CHART_SIZE_RATIO) + "px")
            .style("font-family", "NeueMontreal")
            .style("color", this.darkMode ? "white" : "black")
            .style("fill",'currentColor')
            .text(this.seasonName);

          this.drawImageBase64(
            svg,
            0.25 * CHART_WIDTH,
            0.08 * CHART_WIDTH,
            CHART_WIDTH / 10,
            CHART_WIDTH / 10,
            "https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/" +
              this.teamId +
              ".png"
          );
        }

        // -----------------
        //   Draw BarChart
        // -----------------

        var self = this;
        var bars = g.append("g")
          .selectAll("g")
          .data(d3v4.stack().keys(keys)(this.barChartData))
          .enter().append("g")
            .attr("fill", (d, index) => {
              if(!this.isTotal){
                //If is per90 need to remove '90' on the key
                d.key = d.key.slice(0, -2);
              }
              return this.barChartStats.stats.filter(s => s.code === d.key)[0].color;
            })
          .selectAll("rect")
          .data(function(d) { return d; })
          .enter().append("rect")
            .attr("x", d => {
              if(d[0] > d[1]){
                return scaleX(d[1] - d[0]);
              }else{
                return scaleX(d[0]);
              }
            })
            .attr("y", function(d, i) { return scaleY(d.data.playerName) + scaleY.bandwidth() / 4; })
            .attr("width", function(d, i) {
              if(d[0] > d[1]){
                return Math.abs(scaleX(0) - scaleX(d[1] - d[0]));
              }else{
                return Math.abs(scaleX(d[1]) - scaleX(d[0]));
              }
            })
            .attr("height", scaleY.bandwidth() / 2)
            .attr('stroke', '#000')
            .attr('stroke-width', (cfg.borderStroke * CHART_SIZE_RATIO))


        //-------------------------
        //    TEXT next to bars
        //-------------------------

        // Show total next to the end of the bar
        svg.selectAll("totalText")
          .data(this.barChartData)
          .enter()
          .append("text")
            .attr("x", function(player) { return scaleX(player.total) + margin.left + 5;})
            .attr("y", function(player) { return scaleY(player.playerName) + margin.top + (scaleY.bandwidth()/2) + 4;})
            .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
            .style("font-weight", "bold")
            .style("font-family", "NeueMontreal")
            .style("fill",'currentColor')
            .html((player) => {

              if (this.chartType === 'Distance') {
                if (player.total > 1000)
                  return round(player.total / 1000.0, 2) + " km";
                return (player.total > 0 ? player.total : "") + ' m'
              } else if (this.chartType === 'Speed') {
                return (player.total > 0 ? player.total : "") + ' km/h'
              }
              
              return (player.total > 0 ? player.total : "") + (this.barChartStats.isPercentage ? "%" : ""); 
            });

        // TEXT INSIDE BARS
        // Only if there are more than 1 bars in the stat
        if (keys.length > 1) {

          svg.selectAll("textBar")
            .data(d3v4.stack().keys(keys)(this.barChartData))
            .enter().append("g")
            .selectAll("rect")
            .data(function(d) { return d; })
            .enter().append("text")
                .attr("x", d => {
                  if(d[0] > d[1]){
                    if (d[0] > d[1] > 10 || d[0] < 1)
                      return scaleX(d[1] - d[0]) + margin.left + Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2;
                    else
                      return scaleX(d[1] - d[0]) + margin.left + Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2;
                  }else{
                    if (d[1] - d[0] > 10 || d[1] - d[0] < 1)
                      return scaleX(d[0]) + margin.left + Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2;
                    else
                      return scaleX(d[0]) + margin.left + Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2;
                  }
                })
                .attr("y", function(d, i) { return scaleY(d.data.playerName) + scaleY.bandwidth() / 2 + margin.top + 4; })
                .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO * 0.9) + "px")
                .style("font-weight", "bold")
                .style("font-family", "NeueMontreal")
                .style("color", "white")
                .style("cursor", "pointer")
                .style("text-anchor", "middle")
                .html((d) => {
                    const l = Math.abs(scaleX(d[1]) - scaleX(d[0]));
                    const valueToRet = d[1] - d[0];
                    const minWidth = valueToRet.toString().length * 15;
                    if (l < minWidth * CHART_SIZE_RATIO)
                      return "";

                    if (valueToRet >= 1000)
                      return round(valueToRet / 1000.0, 2);

                    return valueToRet < 1 ? round(valueToRet, 2) : round(valueToRet, 1);
                })
                .on("mouseover", function(d) { tooltip.style("display", null); })
                .on("mouseout", function(d) {
                  tooltip.style("display", "none");

                  d3v4.select(this)
                    .transition()
                    .duration(300)
                    .style("cursor", "pointer")
                    .attr("opacity", '1')
                    .attr("stroke-width", (cfg.borderStroke * CHART_SIZE_RATIO));
                })
                .on("mousemove", function(d) {
                  //Get stat to show the name
                  let stat = this.parentNode.__data__.key;

                  var x = 0
                  if(d[0] > d[1]){
                    x =  margin.left - Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2 + 14 * CHART_SIZE_RATIO
                  }else{
                    x =  scaleX(d[0]) + margin.left / 3 + Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2 - 8 * CHART_SIZE_RATIO;
                  }
                  var y = scaleY(d.data.playerName) + scaleY.bandwidth() / 2 + margin.top - (80 * CHART_SIZE_RATIO)
                  tooltip.attr("transform", "translate(" + x + "," + y + ")");
                  tooltip
                  .select("text")
                  .text(Dictionary[self.language]['team']['overviewTab']['stats'][stat] + ": "
                  + round(d[1]-d[0], DECIMALS) + (self.barChartStats.isPercentage ? "%" : "") + (self.chartType === 'Distance' ? " m" : '') + (self.chartType === 'Speed' ? ' km/h' : '') 
                  + (stat === 'maxAcceleration' ? " m/s²" : '')
                );

                  d3v4.select(this)
                    .transition()
                    .duration(300)
                    .style("cursor", "pointer")
                    .attr("opacity", '0.6')
                    .attr("stroke-width", (cfg.borderStrokeHover * CHART_SIZE_RATIO));
                }
              );
        }

        bars
          .on("mouseover", function(d) { tooltip.style("display", null); })
          .on("mouseout", function(d) {
            tooltip.style("display", "none");

            d3v4.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr("opacity", '1')
              .attr("stroke-width", (cfg.borderStroke * CHART_SIZE_RATIO));
          })
          .on("mousemove", function(d) {
            //Get stat to show the name
            let stat = this.parentNode.__data__.key;

            //var xPosition = d3v4.mouse(this)[0] + margin.left/3 * CHART_SIZE_RATIO;
            //var yPosition = d3v4.mouse(this)[1] - (30 * CHART_SIZE_RATIO);

            var x = 0
            if(d[0] > d[1]){
              x =  margin.left - Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2 + 14 * CHART_SIZE_RATIO
            }else{
              x =  scaleX(d[0]) + margin.left / 3 + Math.abs(scaleX(d[1]) - scaleX(d[0])) / 2 - 8 * CHART_SIZE_RATIO;
            }
            var y = scaleY(d.data.playerName) + scaleY.bandwidth() / 2 + margin.top - (80 * CHART_SIZE_RATIO)
            tooltip.attr("transform", "translate(" + x + "," + y + ")");
            tooltip
                .select("text")
                .text(Dictionary[self.language]['team']['overviewTab']['stats'][stat] + ": "
                + round(d[1]-d[0], DECIMALS) + (self.barChartStats.isPercentage ? "%" : "") + (self.chartType === 'Distance' ? " m" : '') + (self.chartType === 'Speed' ? ' km/h' : '')
                + (stat === 'maxAcceleration' ? " m/s²" : ''))
                

            d3v4.select(this)
              .transition()
              .duration(300)
              .style("cursor", "pointer")
              .attr("opacity", '0.6')
              .attr("stroke-width", (cfg.borderStrokeHover * CHART_SIZE_RATIO));
          }
        );

        // Show totalNegative next to the start of the bar for negative values
        if(containsNegativeValues){
          svg.selectAll("totalNegativeText")
            .data(this.barChartData)
            .enter()
            .append("text")
              .attr("text-anchor", "end")
              .attr("x", function(player) { return scaleX(player.totalNegative) + margin.left - 3;})
              .attr("y", function(player) { return scaleY(player.playerName) + margin.top + (scaleY.bandwidth()/2) + 3;})
              .style("font-size", (cfg.labelFontSize * CHART_SIZE_RATIO) + "px")
              .style("font-weight", "bold")
              .style("font-family", "NeueMontreal")
              .style("fill",'currentColor')
              .html((player) => { return player.totalNegative < 0 ? player.totalNegative : ""; });
        }

        //---------------
        //    LEGEND
        //---------------

        var legend = g.append("g")
            .attr("font-family", "NeueMontreal")
            .attr("font-size", (cfg.legendFontSize * CHART_SIZE_RATIO))
            .attr("text-anchor", "end")
          .selectAll("g")
          .data(keys.slice())
          .enter()
          .append("g")
      	  .attr("transform", function(d, i) {

            if (self.printLogo) {
              return "translate(" +
              (CHART_WIDTH - margin.left - margin.right) +
              "," +
              (((keys.length * CHART_SIZE_RATIO)) + i * (cfg.legendSquareLength * CHART_SIZE_RATIO)) +
              ")"; 
            } else {
              return "translate(" +
              (CHART_WIDTH - margin.left - margin.right) +
              "," +
              ((CHART_HEIGHT - (keys.length * 45 * CHART_SIZE_RATIO)) + i * (cfg.legendSquareLength * CHART_SIZE_RATIO)) +
              ")"; 
            }

        });

        //Set legend squares
        legend.append("rect")
            .attr("x", SVG_WIDTH - margin.left - margin.right - (cfg.legendSquareLength * CHART_SIZE_RATIO) + 2)
            .attr("width", (cfg.legendSquareLength * CHART_SIZE_RATIO))
            .attr("height", (cfg.legendSquareLength * CHART_SIZE_RATIO))
            .attr("fill", d => {
              //If is per90 need to remove '90' on the key
              return this.barChartStats.stats.filter(s => s.code === d.replace('90', ''))[0].color;
            })
            .attr('stroke', '#000')
            .attr('stroke-width', (cfg.borderStroke * CHART_SIZE_RATIO));

        //Set legend labels (stats)
        legend.append("text")
            .attr("x", CHART_WIDTH - (45 * CHART_SIZE_RATIO))
            .attr("y", 9.5)
            .attr("dy", "0.32em")
            .text(d => { return Dictionary[this.language]['team']['overviewTab']['stats'][d.replace('90', '')]; });


        // ------------------
        // Create a TOOLTIP
        // ------------------

        var tooltip = svg.append("g")
          .attr("class", "tooltip")
          .style("display", "none");

        tooltip.append("rect")
          .attr("width", (400 * CHART_SIZE_RATIO))
          .attr("height", (40 * CHART_SIZE_RATIO))
          .attr("rx", (8 * CHART_SIZE_RATIO))
          .attr("ry", (8 * CHART_SIZE_RATIO))
          .attr("fill", "#FFFFFF")
          .style("filter", "url(#drop-shadow)");

        tooltip.append("text")
          .attr("dx", "10em")
          .attr("dy", "1.2em")
          .attr("font-size", (20 * CHART_SIZE_RATIO) + "px")
          .attr("font-weight", "bold")
          .style("text-anchor", "middle");

        var triangle = d3v4.symbol()
                    .type(d3v4.symbolTriangle)
                    .size((50 * CHART_SIZE_RATIO));

        tooltip.append("path")
        .attr("d", triangle)
        .attr("fill", "#FFFFFF")
        .attr("transform", function(d) { return "translate(" + (200 * CHART_SIZE_RATIO) + ", " + (50 * CHART_SIZE_RATIO) + ") rotate(-180)"; });


        //---------------
        //    LOGO
        //---------------

        if(this.printLogo){
          let base_image = new Image();
          base_image.src = this.mediaMode ? require(this.logoCfg === 'white' ? "@/assets/img/driblab_normal_white.png" : "@/assets/img/driblab_normal_color.png") :
           require((this.logoCfg === 'white' || this.darkMode) ? "@/assets/img/driblab_white.png" : "@/assets/img/driblab_color.png");
          base_image.onload = (event) => {
            const dataUrl = getDataUrl(event.currentTarget);
            this.drawImage(svg,
            SVG_WIDTH - LOGO_WIDTH - 50,
            CHART_HEIGHT + margin.top - LOGO_HEIGHT,
            LOGO_WIDTH,
            LOGO_HEIGHT,
            dataUrl);
          }
        }
        //Generate filter to create box shadow for tooltip in D3
        this.generateFilters(svg);

      }else{
        //SVG is empty, then print noDataText
        svg.append("text")
            .attr("x", SVG_WIDTH/2)
            .attr("y", SVG_HEIGHT/2 - 30)
            .attr("opacity", '0.4')
            .attr("text-anchor", "middle")
            .style("font-size", "22px")
            .style("font-family", "NeueMontreal")
            .style("font-weight", "bold")
            .style("color", "gray")
            .text(this.noDataText);
      }

      let font = await loadFonts();
      copyAndAppendElement(svg.node(),font.node(),0,0);
      //Emit when is loaded
      this.$emit("loaded", this.rootElementId);
    },
    drawImage: function(svg, x, y, width, height, img) {
      let imgNode = svg.append("svg:image")
        .attr("x", x)
        .attr("y", y)
        .attr("width", width)
        .attr("height", height)
        .attr("xlink:href", img);
    },
    onResize(){
      let elto = document.getElementById(this.rootElementId);
      if(elto){
        this.$emit("sizeChange", {height: elto.clientHeight, width: elto.clientWidth});
      }
    },
    generateFilters(svg){
      // filters go in defs element
      var defs = svg.append("defs");

      var filter = defs.append("filter")
          .attr("id", "drop-shadow")
          .attr("height", "150%");

      filter.append("feGaussianBlur")
          .attr("in", "SourceAlpha")
          .attr("stdDeviation", 3)
          .attr("result", "blur");


      filter.append("feOffset")
          .attr("in", "blur")
          .attr("dx", 3)
          .attr("dy", 3)
          .attr("result", "offsetBlur");

      filter.append("feComponentTransfer").append("feFuncA").attr("type","linear").attr("slope","0.1");

      var feMerge = filter.append("feMerge");

      feMerge.append("feMergeNode")
          .attr("in", "offsetBlur")
      feMerge.append("feMergeNode")
          .attr("in", "SourceGraphic");
    },
    //Wraps SVG text
    wrap(text, width) {
      text.each(function() {
        var text = d3.select(this),
          words = text.text().split(/\s+/).reverse(),
          word,
          line = [],
          lineNumber = 0,
          lineHeight = 1.2,
          y = text.attr("y"),
          x = text.attr("x"),
          dy = parseFloat(text.attr("dy")),
          tspan = text.text(null).append("tspan").attr("x", x).attr("y", y).attr("dy", dy + "em");

        while (word = words.pop()) {
          line.push(word);
          tspan.text(line.join(" "));
          if (tspan.node().getComputedTextLength() > width) {
            line.pop();
            tspan.text(line.join(" "));
            line = [word];
            tspan = text.append("tspan").attr("x", x).attr("y", y).attr("dy", ++lineNumber * lineHeight + dy + "em").text(word);
          }
        }
      });
    },

  },// End methods
  destroyed() {
    d3.select(`#${this.rootElementId}`).select("svg").remove();
    delete this.observer;
  },
}

</script>

<style>
.axis text {
  font-size: 13px;
  color: #000;

}

.axis path,
.axis line{
  fill: none;
  stroke-width: 2px;
  stroke: #928b8b;
  opacity: 0.6
}

</style>



// WEBPACK FOOTER //
// src/views/base/StackedBarChart.vue